import { createAsyncThunk } from "@reduxjs/toolkit";
import CommunicationApiService from "../apiServices/CommunicationApiService";
import { TemplateId } from "../../constants/MessageActions";
import { ConversationModal, SendMessagePayload } from "../../interfaces/MessageObject";

const sortMessagesByTimestamp = (conversations: ConversationModal[]) => {
  if (conversations) {
    conversations.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });
  }
};

// Thunk to fetch messages from an API
export const fetchMyMessages = createAsyncThunk(
  "myMessages/fetchMyMessages",
  async () => {
    const response: any = await CommunicationApiService.getAllMessages();
    sortMessagesByTimestamp(response.contents[0]?.activeConversations);
    sortMessagesByTimestamp(response.contents[0]?.inActiveConversations);
    return response.contents[0];
  },
);

export const fetchArchivedMessages = createAsyncThunk(
  "myMessages/fetchArchivedMessages",
  async () => {
    const response: any = await CommunicationApiService.getAllArchivedMessages();
    // Helper to extract the list regardless of structure (Backend fix workaround)
    let archivedList = response;

    // Check if it's the nested structure the user just shared
    if (response.contents && response.contents[0]?.inActiveConversations) {
      archivedList = response.contents[0].inActiveConversations;
    }
    // Fallback if it's the other structure
    else if (response.inActiveConversations) {
      archivedList = response.inActiveConversations;
    }

    sortMessagesByTimestamp(archivedList);
    return archivedList;
  },
);

// Authoritative fetch of the "conversations awaiting my reply" count. This is
// the source of truth for the badge; optimistic client updates reconcile to it.
export const fetchYetToRespondCount = createAsyncThunk(
  "myMessages/fetchYetToRespondCount",
  async () => {
    return await CommunicationApiService.getYetToRespondCount();
  },
);

export const sendMessage = createAsyncThunk(
  "myMessages/sendMessage",
  async (
    { messageData }: { messageData: SendMessagePayload },
    { dispatch }, // Destructure dispatch from thunkAPI
  ) => {
    const response: any = await CommunicationApiService.sendMessage(
      messageData.messageContent,
      messageData.conversationId,
      messageData.messageTemplate,
    );
    dispatch(fetchMyMessages()); // Use dispatch from thunkAPI
    // Responding flips a conversation off the "yet to respond" list — reconcile.
    dispatch(fetchYetToRespondCount());
    return response;
  },
);

export const makeOffer = createAsyncThunk(
  "myMessages/makeOffer",
  async ({
    offerPrice,
    productId,
  }: {
    offerPrice: number;
    productId: string;
  }) => {
    const response: any = await CommunicationApiService.makeOffer(
      offerPrice,
      productId,
    );
    return response;
  },
);

export const makeOfferInactive = createAsyncThunk(
  "myMessages/makeOfferInactive",
  async (
    { offerId }: { offerId: string },
    { dispatch }, // Destructure dispatch from thunkAPI
  ) => {
    const response: any = await CommunicationApiService.makeOfferInactive(
      offerId,
    );
    dispatch(fetchMyMessages()); // Refresh messages after marking offer as inactive
    // Archiving can remove a conversation from the "yet to respond" set.
    dispatch(fetchYetToRespondCount());
    return response;
  },
);

const MyMessagesThunk = {
  fetchMyMessages,
  fetchArchivedMessages,
  sendMessage,
  makeOffer,
  makeOfferInactive,
  fetchYetToRespondCount,
};

export default MyMessagesThunk;
